import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

export default function HouseholdIncome({ user, onRefreshUser, onRefresh, defaultCollapsed }) {
  const [recentIncome, setRecentIncome] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ source: '', amount: '', date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [states, setStates] = useState(null);
  const [taxState, setTaxState] = useState(user?.taxState || '');
  const [filingStatus, setFilingStatus] = useState(user?.filingStatus || 'single');
  const [savingTax, setSavingTax] = useState(false);
  const [members, setMembers] = useState(null);
  const [incomeInputs, setIncomeInputs] = useState({});
  const [savingMemberId, setSavingMemberId] = useState(null);

  const loadMembers = useCallback(async () => {
    try {
      const data = await api.getHouseholdMembers();
      setMembers(data);
      const inputs = {};
      data.forEach((m) => { inputs[m.id] = m.annual_income || 0; });
      setIncomeInputs(inputs);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const saveMemberIncome = async (userId) => {
    const amount = parseFloat(incomeInputs[userId]);
    if (isNaN(amount) || amount < 0) return;
    setSavingMemberId(userId);
    try {
      await api.updateMemberAnnualIncome(userId, amount);
      loadMembers();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingMemberId(null);
    }
  };

  useEffect(() => {
    api.getStates().then(setStates).catch(() => setStates({}));
  }, []);

  useEffect(() => {
    setTaxState(user?.taxState || '');
    setFilingStatus(user?.filingStatus || 'single');
  }, [user?.taxState, user?.filingStatus]);

  const saveTaxSettings = async (newState, newFilingStatus) => {
    setSavingTax(true);
    try {
      await api.saveTaxSettings(newState, newFilingStatus);
      onRefreshUser?.();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingTax(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await api.getMagi();
      setRecentIncome(data.recentIncome || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveIncome = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.addIncome({
        source: form.source,
        amount: parseFloat(form.amount),
        date: form.date || undefined,
        notes: form.notes,
      });
      setForm({ source: '', amount: '', date: '', notes: '' });
      setAdding(false);
      load();
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteIncome = async (id) => {
    try {
      await api.deleteIncome(id);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmMatch = async (id, transactionId) => {
    try {
      await api.matchIncomeEntry(id, transactionId);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const dismissMatch = async (id, transactionId) => {
    try {
      await api.dismissIncomeMatch(id, transactionId);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <CollapsiblePanel
      title="💵 Household Income"
      defaultCollapsed={defaultCollapsed}
      headerExtra={
        <button style={styles.addBtn} onClick={(e) => { e.stopPropagation(); setAdding(!adding); }}>
          {adding ? 'Cancel' : '+ Add One-Time Income'}
        </button>
      }
    >
      <p style={styles.helpText}>
        Log income as it changes - a raise, a bonus, a new job. This feeds
        your MAGI/ACA tracker, the investment budgeting suggestions, and the
        Income Tracker on Overview.
      </p>

      <div style={styles.taxSettings}>
        <div style={styles.taxRow}>
          <label style={styles.taxLabel}>Tax state (for take-home estimates)</label>
          <select
            style={styles.taxSelect}
            value={taxState}
            onChange={(e) => { setTaxState(e.target.value); saveTaxSettings(e.target.value, filingStatus); }}
            disabled={savingTax || !states}
          >
            <option value="">Not set</option>
            {states && Object.entries(states).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>
        <div style={styles.taxRow}>
          <label style={styles.taxLabel}>Filing status</label>
          <select
            style={styles.taxSelect}
            value={filingStatus}
            onChange={(e) => { setFilingStatus(e.target.value); saveTaxSettings(taxState, e.target.value); }}
            disabled={savingTax}
          >
            <option value="single">Single</option>
            <option value="married">Married filing jointly</option>
          </select>
        </div>
      </div>

      <div style={styles.annualIncomeSection}>
        <label style={styles.taxLabel}>Annual Income (stated, per person)</label>
        <p style={styles.annualIncomeHelp}>
          Your known salary - toggle this any time you get a raise. This
          becomes the target for the whole year: months without synced
          transaction history are estimated so the year still totals to this
          figure by December, rather than just averaging whatever's been
          tracked so far.
        </p>
        {!members ? (
          <p style={styles.empty}>Loading...</p>
        ) : (
          <div style={styles.memberIncomeList}>
            {members.map((m) => (
              <div key={m.id} style={styles.memberIncomeRow}>
                <span style={styles.memberName}>{m.name || m.email}</span>
                <div style={styles.memberIncomeInputWrap}>
                  <span style={styles.dollarSign}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    style={styles.memberIncomeInput}
                    value={incomeInputs[m.id] ?? 0}
                    onChange={(e) => setIncomeInputs((prev) => ({ ...prev, [m.id]: e.target.value }))}
                  />
                  <span style={styles.perYr}>/yr</span>
                  <button
                    style={styles.saveMemberBtn}
                    onClick={() => saveMemberIncome(m.id)}
                    disabled={savingMemberId === m.id}
                  >
                    {savingMemberId === m.id ? '...' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
            {members.length > 1 && (
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Household Total</span>
                <span style={styles.totalValue}>
                  {fmt.currency(members.reduce((sum, m) => sum + parseFloat(m.annual_income || 0), 0))}/yr
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {adding && (
        <form onSubmit={saveIncome} style={styles.incomeForm}>
          <input
            style={styles.input}
            placeholder="Source (e.g. W2, Dividends)"
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            required
          />
          <input
            style={styles.input}
            type="number"
            placeholder="Amount ($)"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            min="0.01"
            step="0.01"
            required
          />
          <input
            style={styles.input}
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.saveBtn} type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Income'}
          </button>
        </form>
      )}

      {recentIncome === null ? (
        <p style={styles.empty}>Loading...</p>
      ) : recentIncome.length > 0 ? (
        <div style={styles.incomeList}>
          {recentIncome.map((entry) => (
            <div key={entry.id} style={styles.incomeEntry}>
              <div style={styles.incomeRow}>
                <div>
                  <span style={styles.incomeSource}>{entry.source}</span>
                  <span style={styles.incomeDate}>{fmt.shortDate(entry.date)}</span>
                  {entry.reconciled ? (
                    <span style={styles.reconciledBadge}>✓ Reconciled</span>
                  ) : (
                    <span style={styles.unconfirmedBadge}>⏳ Unconfirmed</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={styles.incomeAmt}>{fmt.currency(entry.amount)}</span>
                  <button
                    style={styles.deleteBtn}
                    onClick={() => deleteIncome(entry.id)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {entry.matchCandidate && (
                <div style={styles.matchPrompt}>
                  <span style={styles.matchText}>
                    Is this <strong>{entry.matchCandidate.description}</strong> ({fmt.currency(entry.matchCandidate.amount)}, {fmt.shortDate(entry.matchCandidate.date)}) the deposit that hit your account?
                  </span>
                  <div style={styles.matchActions}>
                    <button
                      style={styles.matchYesBtn}
                      onClick={() => confirmMatch(entry.id, entry.matchCandidate.transactionId)}
                    >
                      Yes, that's it
                    </button>
                    <button
                      style={styles.matchNoBtn}
                      onClick={() => dismissMatch(entry.id, entry.matchCandidate.transactionId)}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !adding && (
          <p style={styles.empty}>
            Add income entries to track your MAGI vs the ACA healthcare limit
            and get accurate investment budgeting suggestions.
          </p>
        )
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  addBtn: {
    background: 'transparent',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '700',
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  taxSettings: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  taxRow: { flex: 1, minWidth: '160px' },
  annualIncomeSection: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.9rem 1rem',
    marginBottom: '1.1rem',
  },
  annualIncomeHelp: {
    color: '#6b7280',
    fontSize: '0.72rem',
    lineHeight: '1.5',
    margin: '0.3rem 0 0.75rem',
  },
  memberIncomeList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  memberIncomeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  memberName: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  memberIncomeInputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  dollarSign: { color: '#6b7280', fontSize: '0.8rem' },
  memberIncomeInput: {
    width: '110px',
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '5px 8px',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  perYr: { color: '#6b7280', fontSize: '0.72rem' },
  saveMemberBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.4rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid #1e2a3a',
  },
  totalLabel: { color: '#9ca3af', fontSize: '0.75rem', fontFamily: "'Syne', sans-serif" },
  totalValue: { color: '#00d4aa', fontSize: '0.85rem', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  taxLabel: {
    display: 'block',
    color: '#6b7280',
    fontSize: '0.7rem',
    marginBottom: '4px',
    fontFamily: "'Syne', sans-serif",
  },
  taxSelect: {
    width: '100%',
    padding: '0.5rem 0.6rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
    boxSizing: 'border-box',
  },
  incomeForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    padding: '0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    border: '1px solid #1e2a3a',
  },
  input: {
    padding: '0.5rem 0.75rem',
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    outline: 'none',
  },
  saveBtn: {
    padding: '0.65rem',
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.82rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  error: {
    color: '#ef4444',
    fontSize: '0.8rem',
    padding: '0.4rem 0.6rem',
    background: 'rgba(239,68,68,0.1)',
    borderRadius: '4px',
  },
  incomeList: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  incomeEntry: {
    background: '#0a0f1e',
    borderRadius: '6px',
    border: '1px solid #1e2a3a',
    overflow: 'hidden',
  },
  incomeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
  },
  reconciledBadge: {
    marginLeft: '0.5rem',
    color: '#00d4aa',
    fontSize: '0.65rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  unconfirmedBadge: {
    marginLeft: '0.5rem',
    color: '#6b7280',
    fontSize: '0.65rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  matchPrompt: {
    background: 'rgba(251, 191, 36, 0.06)',
    borderTop: '1px solid rgba(251, 191, 36, 0.25)',
    padding: '0.6rem 0.75rem',
  },
  matchText: {
    display: 'block',
    color: '#9ca3af',
    fontSize: '0.72rem',
    lineHeight: '1.5',
    marginBottom: '0.5rem',
  },
  matchActions: { display: 'flex', gap: '0.5rem' },
  matchYesBtn: {
    background: '#fbbf24',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  matchNoBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  incomeSource: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    marginRight: '0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
  incomeDate: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  incomeAmt: {
    color: '#00d4aa',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.7rem',
    padding: '2px 4px',
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.8rem',
    textAlign: 'center',
    padding: '0.75rem 0',
    margin: 0,
  },
};
